import type { CatalogDocumentDTO } from '@/shared/api'

/** Accepted upload extensions — mirrors backend SUPPORTED_EXTENSIONS. */
export const ACCEPTED_FILE_EXTENSIONS = '.pdf,.txt,.csv,.xlsx'

/** Friendly comma-separated list shown to the user. */
export const ACCEPTED_FILE_LABEL = 'PDF, TXT, CSV ou XLSX'

/**
 * Defaults suggested by the Step 7.9 spec for local testing.
 *
 * These remain healthcare-friendly so the demo flow (clinica-vital) keeps
 * working out of the box, but the labels/helper text in the upload form are
 * deliberately vertical-agnostic ("patient/client/case/company/project").
 *
 * NOTE for future SaaS plug-and-play (Step ≥ 7.10):
 *   When tenant-specific metadata templates land, this constant should be
 *   replaced by a per-tenant preset resolved from the auth context (e.g.
 *   clinic → patient/treatment, law firm → client/case, consulting →
 *   client/project, internal KB → team/topic, sales enablement →
 *   account/opportunity). The shape stays the same so callers don't change.
 */
export const DEFAULT_UPLOAD_METADATA = {
  entityType: 'patient',
  entityId: 'paciente_ana',
  groupType: 'treatment',
  groupId: 'tratamento_joelho',
} as const

/** Hard cap on derived document_id length so we don't generate giant slugs. */
export const MAX_DOCUMENT_ID_LENGTH = 64

const STATUS_LABEL_PT: Record<string, string> = {
  indexed: 'Indexado',
  processing: 'Processando',
  pending: 'Pendente',
  failed: 'Falhou',
  unknown: 'Desconhecido',
}

export function statusLabel(status: string | null | undefined): string {
  if (!status) return STATUS_LABEL_PT.unknown
  return STATUS_LABEL_PT[status] ?? status
}

export type DocumentStatusTone = 'success' | 'warning' | 'danger' | 'muted'

export function statusTone(status: string | null | undefined): DocumentStatusTone {
  if (!status) return 'muted'
  if (status === 'indexed') return 'success'
  if (status === 'failed') return 'danger'
  if (status === 'processing' || status === 'pending') return 'warning'
  return 'muted'
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/**
 * Derive a sensible default document_id from a filename so the user can leave
 * the field blank and still get a stable id. Mirrors backend
 * ``sanitize_identifier`` behavior loosely (lowercased, alnum + ``_``/``-``).
 *
 * Steps:
 *   1. Strip the trailing extension (``.pdf``, ``.tar.gz`` keeps ``.tar``).
 *   2. Unicode-normalize to NFD and drop combining marks → ``ç`` → ``c``.
 *   3. Lowercase.
 *   4. Replace runs of non-``[a-z0-9]`` characters with a single ``_``.
 *   5. Trim leading/trailing underscores.
 *   6. Cap to {@link MAX_DOCUMENT_ID_LENGTH} so logs/UI stay tidy.
 *   7. Trim again in case the cap landed on an underscore.
 *   8. Fall back to ``"documento"`` for pathological inputs (empty / all
 *      punctuation), never returning an empty id.
 */
export function deriveDocumentIdFromFilename(filename: string): string {
  if (!filename) return 'documento'
  const base = filename.replace(/\.[^.]+$/, '')
  const slug = base
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, MAX_DOCUMENT_ID_LENGTH)
    .replace(/^_+|_+$/g, '')
  return slug || 'documento'
}

export function documentTitle(doc: CatalogDocumentDTO): string {
  if (doc.title && doc.title.trim()) return doc.title.trim()
  return doc.filename?.trim() || doc.document_id
}

/**
 * Step 9.5 — render the entity using the human ``entity_display_name`` when
 * the backend provided one; fall back to the technical ``entity_id`` so the
 * UI never shows an empty cell. Returns ``null`` for reference documents
 * (where ``entity_id`` is also null).
 */
export function entityDisplay(doc: CatalogDocumentDTO): string | null {
  if (doc.entity_display_name && doc.entity_display_name.trim()) {
    return doc.entity_display_name.trim()
  }
  return doc.entity_id ?? null
}

export function groupDisplay(doc: CatalogDocumentDTO): string | null {
  if (doc.group_display_name && doc.group_display_name.trim()) {
    return doc.group_display_name.trim()
  }
  return doc.group_id ?? null
}

/**
 * Sort documents newest-first (indexed_at, falling back to created_at). The
 * backend already orders for us today, but a stable client-side sort keeps the
 * library deterministic after an optimistic insert.
 */
export function sortDocumentsByRecency(
  docs: CatalogDocumentDTO[],
): CatalogDocumentDTO[] {
  return [...docs].sort((a, b) => {
    const aTime = new Date(a.indexed_at ?? a.created_at ?? 0).getTime()
    const bTime = new Date(b.indexed_at ?? b.created_at ?? 0).getTime()
    return bTime - aTime
  })
}

/** Status filter values surfaced in the library toolbar. */
export type DocumentStatusFilter =
  | 'all'
  | 'indexed'
  | 'processing'
  | 'failed'
  | 'unknown'

function normalizeStatusForFilter(
  status: string | null | undefined,
): Exclude<DocumentStatusFilter, 'all'> {
  // Chroma backend leaves status null but only returns indexed rows, so null
  // is bucketed as 'indexed'. Truly unrecognized strings fall into 'unknown'
  // so the user can spot anomalies via the filter dropdown.
  if (!status) return 'indexed'
  if (status === 'indexed') return 'indexed'
  if (status === 'failed') return 'failed'
  if (status === 'processing' || status === 'pending' || status === 'uploading') {
    return 'processing'
  }
  return 'unknown'
}

/** Fold/strip accents for case-insensitive search matching. */
function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

/**
 * Filter the document list by free-text query (matches filename, document_id,
 * entity coordinates, group coordinates) and by status bucket. The query is
 * accent/case-insensitive so "luiza" finds "Luíza" without surprises.
 */
export function filterDocuments(
  docs: CatalogDocumentDTO[],
  query: string,
  status: DocumentStatusFilter,
): CatalogDocumentDTO[] {
  const needle = foldForSearch(query.trim())
  return docs.filter((doc) => {
    if (status !== 'all' && normalizeStatusForFilter(doc.status) !== status) {
      return false
    }
    if (!needle) return true
    const haystack = foldForSearch(
      [
        doc.filename ?? '',
        doc.document_id,
        doc.entity_type,
        doc.entity_id,
        doc.group_type ?? '',
        doc.group_id ?? '',
      ].join(' '),
    )
    return haystack.includes(needle)
  })
}

/**
 * True when a given `document_id` already exists for the same entity/group
 * scope on the current tenant. Used by the upload form to surface a "this
 * upload will REPLACE the existing document" warning, since the backend's
 * ``create_or_reset_document`` overwrites silently for matching coordinates.
 */
export function isDuplicateDocumentId(
  docs: CatalogDocumentDTO[],
  candidate: {
    tenantId: string
    entityType: string
    entityId: string
    groupType: string | null
    groupId: string | null
    documentId: string
  },
): boolean {
  const id = candidate.documentId.trim().toLowerCase()
  if (!id) return false
  return docs.some(
    (d) =>
      d.tenant_id === candidate.tenantId &&
      d.entity_type === candidate.entityType &&
      d.entity_id === candidate.entityId &&
      (d.group_type ?? null) === candidate.groupType &&
      (d.group_id ?? null) === candidate.groupId &&
      d.document_id.trim().toLowerCase() === id,
  )
}

/**
 * Step 13.7-FIX — find an existing catalog row that collides on
 * ``tenant_id + document_id`` (case-insensitive), returning it so the upload UI
 * can name the matched document (title/filename).
 *
 * This is the key the backend actually uses for selection/delete/retrieval —
 * ``find_document_by_id`` and the vector filters key on ``tenant + document_id``
 * (entity/group are metadata). So re-using the same ``document_id`` — even under
 * a different entity/group — is unsafe (it collides at retrieval/delete) and
 * must be surfaced, which the older full-tuple ``isDuplicateDocumentId`` missed.
 *
 * ``docs`` is the tenant-scoped catalog snapshot the caller already holds, so no
 * extra request is made and cross-tenant existence is never probed.
 */
export function findDuplicateDocument(
  docs: CatalogDocumentDTO[],
  tenantId: string,
  documentId: string,
): CatalogDocumentDTO | null {
  const id = documentId.trim().toLowerCase()
  if (!id || !tenantId) return null
  return (
    docs.find(
      (d) =>
        d.tenant_id === tenantId &&
        d.document_id.trim().toLowerCase() === id,
    ) ?? null
  )
}
